<template>
  <div
    :class="[
      'inline-flex items-center justify-center font-medium text-white bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg overflow-hidden',
      sizeClasses,
    ]"
    :style="{ backgroundColor: customColor }"
  >
    <img
      v-if="avatarUrl"
      :src="avatarUrl"
      :alt="alt"
      :class="['w-full h-full object-cover', sizeClasses]"
      @error="onImageError"
    />
    <span v-else :class="textSizeClasses">
      {{ initials }}
    </span>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref } from 'vue';
  import type { Organization } from '../types';

  interface Props {
    organization?: Organization | null;
    name?: string;
    avatarUrl?: string | null;
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
    customColor?: string;
    alt?: string;
  }

  const props = withDefaults(defineProps<Props>(), {
    size: 'md',
    alt: 'Organization avatar',
  });

  const imageError = ref(false);

  // Compute initials based on organization name
  const initials = computed(() => {
    const rawName = props.organization?.name || props.name || '';
    const orgName = rawName.trim();
    if (!orgName) return '?';

    const words = orgName.split(/\s+/).filter(w => w.length > 0);
    if (words.length >= 2) {
      return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
    }
    return orgName.substring(0, 2).toUpperCase();
  });

  // Size classes for different avatar sizes
  const sizeClasses = computed(() => {
    const sizes = {
      xs: 'w-6 h-6',
      sm: 'w-8 h-8',
      md: 'w-10 h-10',
      lg: 'w-12 h-12',
      xl: 'w-16 h-16',
      '2xl': 'w-20 h-20',
    };
    return sizes[props.size];
  });

  // Text size classes for initials
  const textSizeClasses = computed(() => {
    const sizes = {
      xs: 'text-[10px]',
      sm: 'text-xs',
      md: 'text-sm',
      lg: 'text-base',
      xl: 'text-xl',
      '2xl': 'text-2xl',
    };
    return sizes[props.size];
  });

  // Show avatar URL only if provided and no error occurred
  const avatarUrl = computed(() => {
    const url = props.organization?.avatar_url || props.avatarUrl;
    return !imageError.value ? url : null;
  });

  const onImageError = () => {
    imageError.value = true;
  };
</script>
